// MAIN-849: the real review screen — built from Jeff's HTML mockup, wired to
// real persisted proposals/GraphQL instead of the mockup's fake six-item
// array and simulated actions. Nothing here ever writes to the live Menu
// collection except the explicit Publish action at the bottom.
import React, { useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useReactiveVar } from "@apollo/client";
import { Button } from "@mui/material";
import SideMenu from "../../components/organisms/sidebar";
import {
  buildPublishErrorMap,
  buildPublishRequestFailureMessage,
  buildPublishResultMessage,
  getDisplayDiff,
  describeCardCategory,
  getEffectiveProposalFields,
  getImportCounts,
  getProposalReviewState,
  getPublishProblems,
  getReadyToPublish,
  getRemainingMissingFields,
  getVisibleClassificationChips,
} from "./menuImportReview.utils";
import LoggedHeader from "../Common/loggedHeader";
import {
  GET_MENU_IMPORT_JOB,
  LIST_CATEGORY,
  LIST_MENU_IMPORT_PROPOSALS,
} from "../../Graphql/queries";
import {
  ACCEPT_ALL_NEW_MENU_IMPORT_PROPOSALS,
  PUBLISH_MENU_IMPORT_JOB,
  REJECT_ALL_DUPLICATE_MENU_IMPORT_PROPOSALS,
  REVIEW_MENU_IMPORT_PROPOSAL,
} from "../../Graphql/mutations";
import { toggleSnackbar, userDeatils } from "../../ReactiveVariables/index";
import MenuImportProposalEditModal from "./MenuImportProposalEditModal";
import MenuImportFixAllDialog from "./MenuImportFixAllDialog";
import { getFixAllCount } from "./menuImportFixAll.utils";
import "./menu.css";

const CLASSIFICATIONS = ["New", "Duplicate", "Update", "Blocked", "Uncertain"];

const CLASSIFICATION_LABEL = {
  New: "New item",
  Duplicate: "Possible duplicate",
  Update: "Update to an existing item",
  Blocked: "Can't change yet",
  Uncertain: "Needs a closer look",
};

const CLASSIFICATION_BADGE_CLASS = {
  New: "menu-review-badge--new",
  Duplicate: "menu-review-badge--duplicate",
  Update: "menu-review-badge--update",
  Blocked: "menu-review-badge--blocked",
  Uncertain: "menu-review-badge--uncertain",
};

const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

function ProposalCard({
  proposal,
  categories,
  onAccept,
  onReject,
  onUndo,
  onEdit,
  onFix,
  busy,
  publishError,
}) {
  const badgeClass =
    CLASSIFICATION_BADGE_CLASS[proposal.classification] || "menu-review-badge--uncertain";
  const label = CLASSIFICATION_LABEL[proposal.classification] || proposal.classification;
  // MAIN-849: the card shows what will be published — the caterer's edits
  // over what the AI read — and says plainly when an item has been edited.
  const fields = getEffectiveProposalFields(proposal);
  const reviewState = getProposalReviewState(proposal);
  const isPending = reviewState.awaitingDecision;
  // MAIN-879: problems are shown on every item still in play — not only once
  // accepted — so a caterer can fix them before deciding.
  const problems = getPublishProblems(proposal);
  // MAIN-879: website currency/customization limits can't be fixed in the
  // editor either, so an open-order hold is told apart by classification.
  const heldByOrder = proposal.classification === "Blocked";
  const firstFixable = problems.find((problem) => problem.fixable);
  const showProblems = !reviewState.applied && proposal.reviewStatus !== "Rejected";
  const canFix = !heldByOrder && (Boolean(firstFixable) || Boolean(publishError));
  const displayDiff = getDisplayDiff(proposal);
  const diffFields = displayDiff.map((row) => row.field);
  const showDiff = proposal.classification === "Update" && displayDiff.length > 0;
  const stillMissing = getRemainingMissingFields(proposal);
  // MAIN-849 (applied history): a proposal that carries publishedMenuId is
  // already live on the menu. It must read as Applied rather than merely
  // "Accepted", and it must not offer Undo — Undo only resets reviewStatus, it
  // does not unpublish the Menu item, so offering it on a live row promises a
  // reversal that never happens.
  const isApplied = reviewState.applied;

  return (
    <div className={`menu-review-card${isPending ? "" : " settled"}`}>
      <div style={{ flex: 1 }}>
        <span className={`menu-review-badge ${badgeClass}`}>{label}</span>
        {reviewState.showEdited && (
          <span className="menu-review-badge menu-review-badge--edited">Edited</span>
        )}
        <h4 style={{ margin: "0 0 4px" }}>{fields.itemName || "(no name extracted)"}</h4>
        {proposal.websiteImport && proposal.unsupportedCustomization && (
          <div className="menu-review-note">
            Customization needs manual setup: {proposal.unsupportedCustomization}
          </div>
        )}
        <div className="menu-review-meta">
          {describeCardCategory(proposal, categories)} · {fields.dietory || "Dietary not specified"}{" "}
          ·{" "}
          {fields.price != null
            ? proposal.websiteImport && proposal.currency !== "USD"
              ? `${fields.price} ${proposal.currency || "(currency unknown)"}`
              : formatter.format(fields.price)
            : "Price not found"}
          {fields.itemPkgType ? ` · ${fields.itemPkgType}` : ""}
          {fields.minQuantity != null
            ? ` · Min ${fields.minQuantity}${
                proposal.quantityDefaulted && proposal.editedFields?.minQuantity == null
                  ? " (default)"
                  : ""
              }`
            : ""}
        </div>
        {/*
          MAIN-879: a category inferred from the dish name is a suggestion.
          Once the caterer picks one in the editor, their choice wins and the
          note goes away.
        */}
        {proposal.categorySource === "inferred" && !proposal.editedFields?.categoryId && (
          <div className="menu-review-suggested">
            Category suggested from the dish name — check it, or choose another with Edit.
          </div>
        )}
        {fields.itemDescription && !(showDiff && diffFields.includes("itemDescription")) && (
          <div className="menu-review-desc">{fields.itemDescription}</div>
        )}

        {/*
          MAIN-845: say why an item cannot go live, on the item itself. A
          failed publish previously produced only a count, so a caterer had no
          way to learn that (for example) a category was missing.
        */}
        {/*
          MAIN-879: the problem and the way to fix it sit together on the item.
          The Fix control used to live in the status column on the far side
          of the card, away from the message it answered, and only once an
          item had been accepted. One Fix per card; it opens the editor on
          the first field that needs attention. An open-order hold cannot be
          fixed in the editor, so it gets an explanation instead.
        */}
        {showProblems && (publishError || problems.length > 0) && (
          <div className="menu-review-problem" role="alert">
            <div className="menu-review-problem-text">
              {publishError ? <div>Could not publish: {publishError}</div> : null}
              {problems.length > 0 && (
                <div>
                  {heldByOrder
                    ? "Held by an open order — this item can't be changed until those orders are completed or cancelled."
                    : `Needs before publishing: ${problems.map((p) => p.message).join(" · ")}`}
                </div>
              )}
            </div>
            {canFix && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                className="menu-review-fix"
                disabled={busy}
                onClick={() => onFix(firstFixable?.field || null)}>
                Fix
              </Button>
            )}
          </div>
        )}

        {showDiff && (
          <div className="menu-review-diff">
            {displayDiff.map((d) => (
              <div key={d.field}>
                {d.field}: <span className="menu-review-old">{d.oldValue}</span>
                <span className="menu-review-new">{d.newValue}</span>
                {d.edited && <span className="menu-review-edited-note">your edit</span>}
              </div>
            ))}
          </div>
        )}

        {(proposal.classification === "Duplicate" ||
          proposal.classification === "Uncertain" ||
          proposal.classification === "Blocked") &&
          proposal.matchedMenuItem && (
            <div className="menu-review-note">
              Matches your existing <b>{proposal.matchedMenuItem.itemName}</b>
              {proposal.matchedMenuItem.categoryDtls?.categoryTitle
                ? ` · ${proposal.matchedMenuItem.categoryDtls.categoryTitle}`
                : ""}
              {proposal.matchedMenuItem.price != null
                ? ` · ${formatter.format(proposal.matchedMenuItem.price)}`
                : ""}
              {proposal.matchScore != null
                ? ` · ${Math.round(proposal.matchScore * 100)}% match`
                : ""}
            </div>
          )}

        {proposal.classification === "Blocked" && (proposal.matchReasons || []).length > 0 && (
          <div className="menu-review-note">{proposal.matchReasons.join(" ")}</div>
        )}

        {/*
          Once the caterer has edited an item they have taken that closer look;
          only what is still missing after their edits is worth repeating.
        */}
        {proposal.needsAttention && !reviewState.showEdited && (
          <div className="menu-review-note">
            Needs a closer look — some details may be missing or uncertain.
          </div>
        )}
        {reviewState.showEdited && stillMissing.length > 0 && (
          <div className="menu-review-note">Still missing: {stillMissing.join(", ")}</div>
        )}
      </div>

      {isApplied ? (
        <div className="menu-review-state menu-review-state--applied">
          Applied
        </div>
      ) : isPending ? (
        <div className="menu-review-actions">
          <Button
            size="small"
            variant="contained"
            disabled={busy || proposal.classification === "Blocked"}
            onClick={onAccept}>
            {proposal.classification === "Duplicate" ? "Keep both" : "Accept"}
          </Button>
          <Button size="small" variant="outlined" disabled={busy} onClick={onEdit}>
            Edit
          </Button>
          <Button size="small" variant="outlined" disabled={busy} onClick={onReject}>
            {proposal.classification === "Duplicate" ? "Skip" : "Reject"}
          </Button>
        </div>
      ) : (
        <div
          className={`menu-review-state menu-review-state--${
            proposal.reviewStatus === "Accepted" ? "accepted" : "rejected"
          }`}>
          {proposal.reviewStatus}
          {/*
            MAIN-845: an accepted item that still has blockers has to be
            fixable. MAIN-879 moved that Fix control next to the problem it
            answers, above.
          */}
          <button type="button" className="menu-review-undo" onClick={onUndo} disabled={busy}>
            Undo
          </button>
        </div>
      )}
    </div>
  );
}

// MAIN-879: how the website page was read, as stored by the API.
const RETRIEVAL_LABELS = {
  gemini_url_context: "Retrieved by Gemini URL Context",
  server_fetch: "Retrieved by Caterplace",
  server_fetch_fallback: "Retrieved by Caterplace after Gemini URL Context failed",
  ezcater_gemini_url_context: "ezCater storefront read by Gemini URL Context",
  ezcater_partner_api: "ezCater Partner API",
};

// MAIN-847: an ezCater storefront can't be checked against an independent
// copy, so the review always says completeness is unverified.
const isEzCaterImport = (job) => /^ezcater_/.test(job?.retrievalMethod || "");

export default function MenuImportReview() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const user = useReactiveVar(userDeatils);
  const catererId =
    user?.data?.login?.catererId ||
    user?.data?.currentUserDetails?.catererId ||
    user?.data?.createCaterer?.catererId;

  const [filter, setFilter] = useState("All");
  const [editingProposalId, setEditingProposalId] = useState(null);
  // MAIN-879: the field a Fix click should focus in the editor.
  const [editingFocusField, setEditingFocusField] = useState(null);
  // MAIN-879 (Fix All): the bulk fix panel.
  const [fixAllOpen, setFixAllOpen] = useState(false);
  // MAIN-879: a synchronous guard against a second publish from a fast double
  // click, before React has re-rendered the disabled button.
  const publishInFlight = useRef(false);
  // Per-proposal reasons from the last publish attempt, so failures stay
  // visible on the item instead of collapsing into a single count.
  const [publishErrors, setPublishErrors] = useState({});

  const { data: jobData } = useQuery(GET_MENU_IMPORT_JOB, {
    variables: { jobId },
    skip: !jobId,
  });
  const {
    data: proposalsData,
    loading,
    refetch,
  } = useQuery(LIST_MENU_IMPORT_PROPOSALS, {
    variables: { jobId },
    skip: !jobId,
    fetchPolicy: "network-only",
  });
  const { data: categoryData } = useQuery(LIST_CATEGORY, {
    // MAIN-867: reconciliation edits must offer the entire category catalog.
    variables: { sortField: "categoryTitle", limit: 0 },
  });

  const job = jobData?.getMenuImportJob;
  const proposals = useMemo(
    () => proposalsData?.listMenuImportProposals || [],
    [proposalsData],
  );
  const categories = categoryData?.listCategory?.data || [];
  // Items with problems the editor can fix — the same ones that show a Fix
  // button. Counted from the persisted items, so it drops as fixes are saved.
  const fixAllCount = getFixAllCount(proposals);

  const [reviewProposal, { loading: reviewing }] = useMutation(REVIEW_MENU_IMPORT_PROPOSAL, {
    onError: (err) =>
      toggleSnackbar({
        status: true,
        message: err?.message || "Could not update this item",
        variant: "error",
      }),
  });
  const [acceptAllNew] = useMutation(ACCEPT_ALL_NEW_MENU_IMPORT_PROPOSALS, {
    onCompleted: () => {
      refetch();
      toggleSnackbar({ status: true, message: "Accepted all new items", variant: "success" });
    },
    onError: (err) =>
      toggleSnackbar({
        status: true,
        message: err?.message || "Could not accept all new items",
        variant: "error",
      }),
  });
  const [rejectAllDuplicate] = useMutation(REJECT_ALL_DUPLICATE_MENU_IMPORT_PROPOSALS, {
    onCompleted: () => {
      refetch();
      toggleSnackbar({ status: true, message: "Rejected all duplicates", variant: "success" });
    },
    onError: (err) =>
      toggleSnackbar({
        status: true,
        message: err?.message || "Could not reject all duplicates",
        variant: "error",
      }),
  });
  // MAIN-879: the menu page reads ListMenu (and the category order) cache-
  // first, and refetchQueries by name only reaches queries that are mounted —
  // which the menu page is not while the caterer is here. So new items stayed
  // invisible until a full browser refresh. Evicting those cached lists makes
  // every route back to the menu page fetch them fresh.
  const [publishJob, { loading: publishing }] = useMutation(PUBLISH_MENU_IMPORT_JOB, {
    update: (cache, { data }) => {
      const applied = (data?.publishMenuImportJob?.results || []).some(
        (r) => r.status === "Applied",
      );
      if (!applied) return;
      cache.evict({ id: "ROOT_QUERY", fieldName: "listMenu" });
      cache.evict({ id: "ROOT_QUERY", fieldName: "listCategoryOrder" });
      cache.gc();
    },
    refetchQueries: ["ListMenuImportJobs"],
  });

  /**
   * MAIN-879: publish only what is ready, then read the proposals back before
   * saying anything, so the message, the counters and the Publish button all
   * come from the server's state — never from a local decrement.
   */
  async function handlePublish() {
    if (publishInFlight.current) return;
    publishInFlight.current = true;
    const proposalIds = getReadyToPublish(proposals).map((p) => p._id);
    try {
      let results;
      try {
        const res = await publishJob({ variables: { catererId, jobId, proposalIds } });
        results = res?.data?.publishMenuImportJob?.results || [];
      } catch (err) {
        // The outcome is unknown: refresh, and say so rather than guessing.
        await refetch();
        toggleSnackbar({ status: true, ...buildPublishRequestFailureMessage(err) });
        return;
      }
      const fresh = await refetch();
      const freshProposals = fresh?.data?.listMenuImportProposals || [];
      const after = getImportCounts(freshProposals);
      const outcome = buildPublishResultMessage(results, { remaining: after.remaining });
      toggleSnackbar({ status: true, message: outcome.message, variant: outcome.variant });
      setPublishErrors(buildPublishErrorMap(results));
      // Everything is live: back to the menu, where the new items now load.
      if (outcome.kind === "success" || outcome.kind === "already") navigate("/menu");
    } finally {
      publishInFlight.current = false;
    }
  }

  const counts = useMemo(() => {
    const base = { All: proposals.length };
    CLASSIFICATIONS.forEach((key) => {
      base[key] = 0;
    });
    proposals.forEach((p) => {
      if (base[p.classification] !== undefined) base[p.classification] += 1;
    });
    return base;
  }, [proposals]);

  // MAIN-879: one set of counters for the whole screen, from the persisted
  // proposals. "43 items" used to stay 43 after two had been published.
  const importCounts = getImportCounts(proposals);
  const editedCount = proposals.filter(
    (p) => p.reviewStatus === "Edited" && !p.publishedMenuId,
  ).length;
  // Accepted still means "I want this item"; it does not mean it can go
  // live yet. Publish must not imply otherwise.
  const readyCount = importCounts.readyToPublish;
  // A fully applied job has nothing left to publish. Publishing again is a
  // harmless no-op server-side, but offering the button invites a pointless
  // click and implies work is still outstanding.
  const jobApplied = job?.status === "APPLIED";
  const incompleteCount = importCounts.needsFixes;

  const visibleProposals =
    filter === "All" ? proposals : proposals.filter((p) => p.classification === filter);

  async function handleAction(proposal, action) {
    await reviewProposal({ variables: { proposalId: proposal._id, action } });
    refetch();
  }

  async function handleSaveEdit(editedFields) {
    if (!editingProposalId) return;
    await reviewProposal({
      variables: { proposalId: editingProposalId, action: "Edit", editedFields },
    });
    setEditingProposalId(null);
    refetch();
  }

  const editingProposal = proposals.find((p) => p._id === editingProposalId) || null;

  return (
    <div>
      <LoggedHeader title="Review Import" />
      <SideMenu selected="Menu" />
      <div className="contentWrap">
        <div className="contentWrapInner">
          <div className="menu-review-head">
            <div>
              <h4 className="h4">Review imported items</h4>
              <p>
                {job?.sourceType === "WebsiteUrl"
                  ? "From your menu website"
                  : job?.sourceType === "EzCaterUrl"
                    ? "From your EZ Cater page"
                    : "From your uploaded PDF"}
                {" · "}Nothing changes on your live menu until you publish.
              </p>
            </div>
          </div>

          {job?.sourceType === "WebsiteUrl" && (
            <p>
              Source: {job.sourceUrl}
              {RETRIEVAL_LABELS[job.retrievalMethod]
                ? ` · ${RETRIEVAL_LABELS[job.retrievalMethod]}`
                : ""}
            </p>
          )}
          {isEzCaterImport(job) && (
            <div className="menu-review-warning" role="alert">
              Completeness not verified. Compare these items, prices and packaging with
              your ezCater menu before publishing.
            </div>
          )}
          {job?.extractionNotes && <p role="status">{job.extractionNotes}</p>}
          <div className="menu-review-filters">
            <button
              type="button"
              className={`menu-review-chip${filter === "All" ? " on" : ""}`}
              onClick={() => setFilter("All")}>
              All {counts.All}
            </button>
            {getVisibleClassificationChips(CLASSIFICATIONS, counts, filter).map((key) => (
              <button
                key={key}
                type="button"
                className={`menu-review-chip${filter === key ? " on" : ""}`}
                onClick={() => setFilter(key)}>
                {CLASSIFICATION_LABEL[key]} {counts[key]}
              </button>
            ))}
          </div>

          {loading ? (
            <p>Loading…</p>
          ) : visibleProposals.length === 0 ? (
            <p>No items in this filter.</p>
          ) : (
            visibleProposals.map((proposal) => (
              <ProposalCard
                key={proposal._id}
                proposal={proposal}
                categories={categories}
                onAccept={() => handleAction(proposal, "Accept")}
                onReject={() => handleAction(proposal, "Reject")}
                onUndo={() => handleAction(proposal, "Undo")}
                onEdit={() => {
                  setEditingFocusField(null);
                  setEditingProposalId(proposal._id);
                }}
                onFix={(field) => {
                  setEditingFocusField(field);
                  setEditingProposalId(proposal._id);
                }}
                busy={reviewing}
                publishError={publishErrors[proposal._id]}
              />
            ))
          )}

          {fixAllOpen && (
            <MenuImportFixAllDialog
              proposals={proposals}
              categories={categories}
              onClose={() => setFixAllOpen(false)}
              onSaved={refetch}
            />
          )}

          {editingProposal && (
            <MenuImportProposalEditModal
              proposal={editingProposal}
              categories={categories}
              focusField={editingFocusField}
              onCancel={() => setEditingProposalId(null)}
              onSave={handleSaveEdit}
              saving={reviewing}
            />
          )}
        </div>

        <div className="menu-review-publish-bar">
          <div className="sum">
            <div className="menu-review-totals">
              <b>{importCounts.total}</b> imported · <b>{importCounts.published}</b> published ·{" "}
              <b>{importCounts.remaining}</b> remaining
            </div>
            <div className="menu-review-breakdown">
              {importCounts.awaitingReview} awaiting review · {importCounts.readyToPublish} ready to
              publish
              {importCounts.needsFixes ? ` · ${importCounts.needsFixes} need fixes` : ""}
              {importCounts.rejected ? ` · ${importCounts.rejected} rejected` : ""}
              {importCounts.blocked ? ` · ${importCounts.blocked} held by open orders` : ""}
            </div>
            {editedCount ? (
              <div className="menu-review-hint">
                {editedCount} edited {editedCount === 1 ? "item is" : "items are"} not accepted yet.
                Accept {editedCount === 1 ? "it" : "them"} to include{" "}
                {editedCount === 1 ? "it" : "them"} when you publish.
              </div>
            ) : null}
            {incompleteCount ? (
              <div className="menu-review-warning">
                {incompleteCount} accepted{" "}
                {incompleteCount === 1 ? "item needs" : "items need"} fixing
                before {incompleteCount === 1 ? "it" : "they"} can go live.
              </div>
            ) : null}
          </div>
          <div className="menu-review-bulk-actions">
            <Button
              variant="outlined"
              color="error"
              className="menu-review-fix-all"
              // Suggestions need the category list, so wait for it.
              disabled={!fixAllCount || !categoryData || loading}
              onClick={() => setFixAllOpen(true)}>
              Fix All{fixAllCount ? ` (${fixAllCount})` : ""}
            </Button>
            <Button
              variant="outlined"
              disabled={!counts.New}
              onClick={() => acceptAllNew({ variables: { jobId } })}>
              Accept all New
            </Button>
            <Button
              variant="outlined"
              disabled={!counts.Duplicate}
              onClick={() => rejectAllDuplicate({ variables: { jobId } })}>
              Reject all Duplicates
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!readyCount || publishing || jobApplied}
              onClick={handlePublish}>
              {publishing
                ? "Publishing…"
                : jobApplied
                  ? "Published"
                  : `Publish${readyCount ? ` ${readyCount}` : ""}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
